<template>
  <div class="reg-root">
  <head>
    <meta charset="utf-8" /><title>FLOCKR | Register</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="/assets/images/favicon.svg">
    <link href="/assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="/assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="/assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="/assets/css/custom.css" rel="stylesheet" type="text/css" />
  </head>
  <body data-sidebar="dark">
    <div class="reg-wrapper">
      <div class="reg-card">
        <!-- Logo -->
        <div class="text-center mb-4">
          <img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:44px;cursor:pointer;" @click="$router.push('/')" />
          <p class="mt-2" style="color:var(--fm-s,#6B6356);font-size:13px;">Africa's Livestock Marketplace</p>
        </div>

        <!-- ===== STEP 1: REGISTRATION FORM ===== -->
        <div v-if="step === 1">
          <h5 class="text-center fw-bold mb-4" style="color:var(--fm-h,#F5F0E8);">Create Account</h5>

          <div class="row g-3 mb-3">
            <div class="col-6">
              <label class="auth-label">First Name <span class="text-danger">*</span></label>
              <input v-model="form.firstName" type="text" class="form-control auth-input" placeholder="John" />
            </div>
            <div class="col-6">
              <label class="auth-label">Last Name <span class="text-danger">*</span></label>
              <input v-model="form.lastName" type="text" class="form-control auth-input" placeholder="Mwangi" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Phone Number <span class="text-danger">*</span></label>
            <div class="input-group">
              <select v-model="countryCode" class="form-select" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:600;max-width:110px;font-size:13px;padding:6px 8px;">
                <option v-for="c in countryCodes" :key="c.code" :value="c.code">{{ c.flag }} +{{ c.code }}</option>
              </select>
              <input v-model="form.phone" type="tel" class="form-control auth-input" placeholder="712 345 678" maxlength="15" @input="autoFixPhone" style="border-left:none;" />
            </div>
            <div v-if="form.phone && phonePreview" style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:3px;">Will register as: <strong style="color:#BFA164;">+{{ phonePreview }}</strong></div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Email <span class="text-danger">*</span></label>
            <input v-model="form.email" type="email" class="form-control auth-input" placeholder="john@example.com" />
            <div style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:3px;">
              <i class="mdi mdi-information-outline me-1"></i>Your verification code will be sent to <strong style="color:#BFA164;">both your phone (SMS) and email</strong>. Use whichever arrives first.
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Password <span class="text-danger">*</span></label>
            <input :type="showPassword ? 'text' : 'password'" v-model="form.password" class="form-control auth-input" placeholder="Min 6 characters" />
            <div class="d-flex align-items-center gap-2 mt-1">
              <input type="checkbox" id="showPwd" v-model="showPassword" style="accent-color:#BFA164;" />
              <label for="showPwd" style="color:var(--fm-s,#6B6356);font-size:11px;margin:0;">Show password</label>
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Your Location <span class="text-danger">*</span></label>
            <div class="d-flex gap-2">
              <input v-model="form.address" type="text" class="form-control auth-input flex-fill" placeholder="City, country (or tap GPS button)" @input="onAddressInput" />
              <button type="button" class="btn btn-sm btn-outline-warning" @click="useMyLocation" :disabled="locBusy" title="Use my current location">
                <span v-if="locBusy"><span class="spinner-border spinner-border-sm"></span></span>
                <i v-else class="mdi mdi-crosshairs-gps"></i>
              </button>
            </div>
            <div v-if="form.latitude" style="color:#50C878;font-size:10px;margin-top:3px;"><i class="mdi mdi-map-marker-check me-1"></i>Location set: {{ Number(form.latitude).toFixed(4) }}, {{ Number(form.longitude).toFixed(4) }}</div>
            <div v-else style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:3px;">Available worldwide — tap GPS or type your city. We use this to match you with nearby vets, transporters, and buyers.</div>
            <div v-if="locError" class="mt-2 p-2" style="background:rgba(255,152,0,0.08);border:1px solid rgba(255,152,0,0.3);border-radius:6px;">
              <div style="color:#FF9800;font-size:11px;line-height:1.4;"><i class="mdi mdi-alert-outline me-1"></i>{{ locError }}</div>
              <button type="button" class="btn btn-sm btn-warning text-dark fw-bold mt-2" @click="useMyLocation" :disabled="locBusy">
                <span v-if="locBusy"><span class="spinner-border spinner-border-sm me-1"></span> Trying…</span>
                <span v-else><i class="mdi mdi-crosshairs-gps me-1"></i> Allow location &amp; try again</span>
              </button>
              <div style="color:var(--fm-s,#6B6356);font-size:10px;margin-top:6px;line-height:1.4;">
                If your browser keeps blocking, tap the lock/info icon in your address bar → Site settings → set <strong style="color:#BFA164;">Location</strong> to <strong style="color:#BFA164;">Allow</strong>, then come back and tap the button.
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Referral Code <span style="color:var(--fm-s,#6B6356);font-weight:400;">(optional)</span></label>
            <input v-model="form.referralCode" type="text" class="form-control auth-input" placeholder="ABC12345" />
          </div>

          <!-- hCaptcha anti-bot widget — only renders if hcaptcha_enabled=1 + site key is set on the backend.
               When the *sandbox* key is in use, hCaptcha injects a red "for testing only" warning inside
               the widget that needs extra vertical room — so we reserve min-height + overflow:visible. -->
          <div v-if="hcaptchaSiteKey" class="mt-3 mb-2" style="display:flex;justify-content:center;min-height:120px;overflow:visible;">
            <!-- Rendered explicitly via hcaptcha.render() in ensureHCaptchaWidget() so the widget
                 always appears even when the script loads before Vue has painted this container.
                 The class-based auto-scan on hCaptcha's side has a race on mobile. -->
            <div id="hcaptcha-container" ref="hcaptchaContainer"></div>
          </div>

          <div v-if="errorMsg" class="alert alert-danger py-2 px-3" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;">
            <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
          </div>

          <button class="btn btn-warning w-100 mt-2" @click="register" :disabled="!canRegister || submitting">
            <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Creating Account...</span>
            <span v-else><i class="mdi mdi-account-plus me-1"></i> Create Account</span>
          </button>

          <!-- Google Sign-In -->
          <div v-if="googleEnabled" class="reg-divider"><span>or</span></div>
          <div v-if="googleEnabled" class="reg-google-wrap">
            <div ref="googleBtn"></div>
            <div v-if="googleBusy" class="text-center mt-2" style="color:#A09888;font-size:12px;"><span class="spinner-border spinner-border-sm me-1"></span>Signing up with Google…</div>
            <div v-if="googleError" class="text-center mt-2" style="color:#FF6B6B;font-size:12px;">{{ googleError }}</div>
          </div>

          <div class="text-center mt-4" style="color:var(--fm-s,#6B6356);font-size:13px;">
            Already have an account? <a href="javascript:void(0)" @click="$router.push('/login')" style="color:#BFA164;font-weight:600;text-decoration:none;">Sign In</a>
          </div>
        </div>

        <!-- ===== STEP 2: OTP VERIFICATION ===== -->
        <div v-if="step === 2">
          <div class="text-center mb-4">
            <div style="width:64px;height:64px;border-radius:50%;background:rgba(191,161,100,.1);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px;">
              <i class="mdi mdi-cellphone-message" style="font-size:28px;color:#BFA164;"></i>
            </div>
            <h5 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">Verify Your Phone</h5>
            <p style="color:var(--fm-s,#6B6356);font-size:13px;line-height:1.6;">We sent a 6-digit verification code to:</p>
            <div style="background:rgba(191,161,100,0.08);border:1px solid rgba(191,161,100,0.3);border-radius:10px;padding:10px 14px;margin-bottom:12px;text-align:left;">
              <div style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--fm-h,#F5F0E8);"><i class="mdi mdi-cellphone-message" style="color:#BFA164;font-size:18px;"></i><strong>SMS:</strong>&nbsp;+{{ phonePreview }}</div>
              <div style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--fm-h,#F5F0E8);margin-top:4px;"><i class="mdi mdi-email-outline" style="color:#BFA164;font-size:18px;"></i><strong>Email:</strong>&nbsp;{{ form.email }}</div>
            </div>
            <p style="color:var(--fm-s,#6B6356);font-size:11px;margin-bottom:12px;">Check your inbox + spam folder. Use whichever code arrives first.</p>
          </div>

          <div class="mb-3">
            <label class="auth-label">Enter OTP Code</label>
            <input v-model="otpCode" type="text" class="form-control auth-input text-center" placeholder="000000" maxlength="6" style="font-size:24px;letter-spacing:8px;font-weight:700;" @keyup.enter="verifyOtp" />
          </div>

          <div v-if="otpError" class="alert alert-danger py-2 px-3" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;">
            <i class="mdi mdi-alert-circle me-1"></i> {{ otpError }}
          </div>

          <button class="btn btn-warning w-100 mt-2" @click="verifyOtp" :disabled="otpCode.length < 6 || verifying">
            <span v-if="verifying"><span class="spinner-border spinner-border-sm me-1"></span> Verifying...</span>
            <span v-else><i class="mdi mdi-check me-1"></i> Verify Phone</span>
          </button>

          <div class="text-center mt-3">
            <a href="javascript:void(0)" @click="step=1" style="color:var(--fm-s,#6B6356);font-size:12px;text-decoration:none;"><i class="mdi mdi-arrow-left me-1"></i>Back to registration</a>
          </div>
        </div>

        <!-- ===== STEP 3: SUCCESS ===== -->
        <div v-if="step === 3">
          <div class="text-center">
            <div style="width:64px;height:64px;border-radius:50%;background:rgba(80,200,120,.1);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px;">
              <i class="mdi mdi-check-circle" style="font-size:32px;color:#50C878;"></i>
            </div>
            <h5 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">Account Created!</h5>
            <p style="color:var(--fm-m,#A09888);font-size:13px;">Welcome to Flockr, {{ form.firstName }}. Your account is ready.</p>
            <button class="btn btn-warning w-100 mt-3" @click="$router.push('/login')">
              <i class="mdi mdi-login me-1"></i> Sign In Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </body>
  </div>
</template>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style scoped>
.reg-root{background:#0A0A0A!important;min-height:100vh}
.reg-wrapper{display:flex;align-items:center;justify-content:center;min-height:100vh;padding:20px}
.reg-card{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);border-radius:16px;padding:40px 32px;width:100%;max-width:480px}
.auth-label{font-size:13px;font-weight:600;color:#D4C9B8!important;margin-bottom:6px;display:block}
.auth-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#2A2520);color:var(--fm-h,#F5F0E8)!important;border-radius:8px}
.auth-input:focus{border-color:#BFA164;background:var(--fm-bg2,#0F0F0F)!important;color:var(--fm-h,#F5F0E8)!important;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.auth-input::placeholder{color:#4A4540!important}
.auth-input option{background:var(--fm-bg1,#141414)!important;color:var(--fm-h,#F5F0E8)!important}
.reg-type-btn{padding:8px 16px;border-radius:8px;border:1px solid var(--fm-br,#2A2520);background:var(--fm-bg2,#0F0F0F)!important;color:var(--fm-m,#A09888)!important;font-size:13px;cursor:pointer;transition:all .2s;flex:1}
.reg-type-btn:hover{border-color:#BFA164;color:var(--fm-h,#F5F0E8)!important}
.reg-type-btn.active{background:rgba(191,161,100,.15)!important;border-color:#BFA164;color:#BFA164!important;font-weight:600}
@media(max-width:480px){.reg-card{padding:28px 20px;border-radius:12px}}
.reg-divider{display:flex;align-items:center;gap:10px;margin:18px 0 12px;color:var(--fm-s,#6B6356);font-size:11px;text-transform:uppercase;letter-spacing:1px}
.reg-divider::before,.reg-divider::after{content:'';flex:1;height:1px;background:var(--fm-br,#1E1E1E)}
html[data-theme="light"] .reg-divider::before,html[data-theme="light"] .reg-divider::after{background:#E0D8D0}
.reg-google-wrap{display:flex;flex-direction:column;align-items:center}
</style>
<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "Register",
  data(){return{
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',

    step:1,submitting:false,verifying:false,errorMsg:'',otpError:'',showPassword:false,otpCode:'',
    googleEnabled:false,googleClientId:'',googleBusy:false,googleError:'',
    hcaptchaSiteKey:'', hcaptchaToken:'',
    userId:'',countryCode:'254',
    form:{firstName:'',lastName:'',phone:'',email:'',password:'',userType:'buyer',countyId:'',referralCode:'',address:'',latitude:'',longitude:''},
    locBusy:false, locError:'', _locTimer:null,
    counties:[],
    countryCodes:[
      {code:'254',name:'Kenya',flag:'\ud83c\uddf0\ud83c\uddea'},{code:'256',name:'Uganda',flag:'\ud83c\uddfa\ud83c\uddec'},{code:'255',name:'Tanzania',flag:'\ud83c\uddf9\ud83c\uddff'},
      {code:'250',name:'Rwanda',flag:'\ud83c\uddf7\ud83c\uddfc'},{code:'257',name:'Burundi',flag:'\ud83c\udde7\ud83c\uddee'},{code:'251',name:'Ethiopia',flag:'\ud83c\uddea\ud83c\uddf9'},
      {code:'252',name:'Somalia',flag:'\ud83c\uddf8\ud83c\uddf4'},{code:'211',name:'South Sudan',flag:'\ud83c\uddf8\ud83c\uddf8'},{code:'243',name:'DR Congo',flag:'\ud83c\udde8\ud83c\udde9'},
      {code:'234',name:'Nigeria',flag:'\ud83c\uddf3\ud83c\uddec'},{code:'233',name:'Ghana',flag:'\ud83c\uddec\ud83c\udded'},{code:'27',name:'South Africa',flag:'\ud83c\uddff\ud83c\udde6'},
      {code:'260',name:'Zambia',flag:'\ud83c\uddff\ud83c\uddf2'},{code:'263',name:'Zimbabwe',flag:'\ud83c\uddff\ud83c\uddfc'},{code:'265',name:'Malawi',flag:'\ud83c\uddf2\ud83c\uddfc'},
      {code:'258',name:'Mozambique',flag:'\ud83c\uddf2\ud83c\uddff'},{code:'249',name:'Sudan',flag:'\ud83c\uddf8\ud83c\udde9'},{code:'20',name:'Egypt',flag:'\ud83c\uddea\ud83c\uddec'},
      {code:'212',name:'Morocco',flag:'\ud83c\uddf2\ud83c\udde6'},{code:'237',name:'Cameroon',flag:'\ud83c\udde8\ud83c\uddf2'},
      {code:'44',name:'United Kingdom',flag:'\ud83c\uddec\ud83c\udde7'},{code:'1',name:'USA/Canada',flag:'\ud83c\uddfa\ud83c\uddf8'},
      {code:'91',name:'India',flag:'\ud83c\uddee\ud83c\uddf3'},{code:'86',name:'China',flag:'\ud83c\udde8\ud83c\uddf3'},{code:'971',name:'UAE',flag:'\ud83c\udde6\ud83c\uddea'},
      {code:'966',name:'Saudi Arabia',flag:'\ud83c\uddf8\ud83c\udde6'},{code:'49',name:'Germany',flag:'\ud83c\udde9\ud83c\uddea'},{code:'33',name:'France',flag:'\ud83c\uddeb\ud83c\uddf7'}
    ]
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    canRegister(){return this.form.firstName&&this.form.lastName&&this.form.phone&&this.form.email&&/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)&&this.form.password.length>=6&&!!this.form.latitude&&!!this.form.longitude},
    phonePreview(){return this.formatPhone(this.form.phone)}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    if(this.$route.query.phone){this.form.phone=this.$route.query.phone.replace(/^254/,'').replace(/^0/,'');}
    // Prefill referral code from shared link (?ref=CODE). Falls back to sessionStorage so
    // a referral captured on the landing page survives the redirect through login → register.
    var refFromQuery=(this.$route.query.ref||this.$route.query.referral||'').toString().trim().toUpperCase();
    if(refFromQuery){this.form.referralCode=refFromQuery;try{sessionStorage.setItem('pending_referral_code',refFromQuery);}catch(_){}}
    else{try{var stored=sessionStorage.getItem('pending_referral_code');if(stored)this.form.referralCode=stored;}catch(_){}}
    this.fetchCounties();
    this.initGoogleSignIn();
    this.initHCaptcha();
    // Global callbacks (hCaptcha invokes them by name from the data- attributes)
    var self=this;
    window.onHCaptchaSuccess=function(token){self.hcaptchaToken=token;};
    window.onHCaptchaExpired=function(){self.hcaptchaToken='';};
  },
  methods:{
    autoFixPhone(){
      var p=this.form.phone.replace(/[^0-9]/g,'');
      if(p.startsWith('0'))p=p.substring(1);
      if(p.startsWith(this.countryCode))p=p.substring(this.countryCode.length);
      if(p.startsWith('+'))p=p.substring(1);
      this.form.phone=p;
    },
    formatPhone(raw){
      var p=(raw||'').replace(/[^0-9]/g,'');
      if(p.startsWith('0'))p=p.substring(1);
      if(p.startsWith(this.countryCode))p=p.substring(this.countryCode.length);
      return p?this.countryCode+p:'';
    },
    async fetchCounties(){
      try{var r=await executeJson({function:"getCounties",countryCode:"KE"},flocker);if(r.data.success)this.counties=r.data.data.counties||[]}catch(e){console.error(e)}
    },
    useMyLocation(){
      if(!navigator.geolocation){this.locError='Geolocation not available on this device';return}
      var self=this; this.locBusy=true; this.locError='';
      navigator.geolocation.getCurrentPosition(async function(p){
        self.form.latitude=p.coords.latitude;
        self.form.longitude=p.coords.longitude;
        try{
          var r=await fetch('https://nominatim.openstreetmap.org/reverse?format=json&lat='+p.coords.latitude+'&lon='+p.coords.longitude,{headers:{'Accept-Language':'en'}});
          var j=await r.json();
          if(j&&j.display_name) self.form.address=j.display_name;
          // Auto-match Kenya county_id if available
          if(j&&j.address&&j.address.country_code==='ke' && self.counties.length){
            var county=(j.address.county||j.address.state||'').replace(/County$/i,'').trim().toLowerCase();
            var match=self.counties.find(function(c){return (c.name||'').toLowerCase()===county});
            if(match) self.form.countyId=match.id;
          }
        }catch(e){}
        self.locBusy=false;
      },function(){self.locBusy=false;self.locError='Location permission denied. You can type your address instead.'},{timeout:8000});
    },
    onAddressInput(){
      // Clear locked GPS coords if they manually edit; allow them to set fresh
      this.form.latitude=''; this.form.longitude='';
      var self=this;
      if(this._locTimer)clearTimeout(this._locTimer);
      var q=(this.form.address||'').trim();
      if(q.length<4)return;
      this._locTimer=setTimeout(async function(){
        try{
          var r=await fetch('https://nominatim.openstreetmap.org/search?format=json&countrycodes=ke&limit=1&q='+encodeURIComponent(q),{headers:{'Accept-Language':'en'}});
          var j=await r.json();
          if(j&&j.length){
            self.form.latitude=Number(j[0].lat);
            self.form.longitude=Number(j[0].lon);
            self.locError='';   // clear stale GPS-denied message once we resolve via address
          }
        }catch(e){}
      },800);
    },
    async register(){
      this.submitting=true;this.errorMsg='';
      try{
        var phone=this.formatPhone(this.form.phone);
        if(!phone){this.errorMsg='Please enter a valid phone number';this.submitting=false;return}
        var params={function:"registerUser",phone:phone,password:this.form.password,firstName:this.form.firstName,lastName:this.form.lastName,userType:this.form.userType};
        if(this.form.email)params.email=this.form.email;
        if(this.form.countyId)params.countyId=this.form.countyId;
        if(this.form.address)params.addressDetails=this.form.address;
        if(this.form.latitude)params.latitude=this.form.latitude;
        if(this.form.longitude)params.longitude=this.form.longitude;
        if(this.form.referralCode)params.referralCode=this.form.referralCode;
        if(this.hcaptchaSiteKey){
          if(!this.hcaptchaToken){this.errorMsg='Please complete the CAPTCHA to continue';this.submitting=false;return}
          params.hcaptchaToken=this.hcaptchaToken;
        }
        var r=await executeJson(params,flocker);
        if(r.data.success){
          // Phone verification is now optional — server returns user+token on register,
          // so we sign the user in immediately and drop them on the dashboard.
          var user = r.data.data && r.data.data.user;
          var token = r.data.data && r.data.data.token;
          if(user && token){
            var roles = (r.data.data.roles)||[];
            sessionStorage.setItem('userId', user.id);
            sessionStorage.setItem('isLoggedIn', 'true');
            sessionStorage.setItem('userName', user.display_name || '');
            sessionStorage.setItem('userPhone', user.phone || '');
            sessionStorage.setItem('userType', user.user_type || '');
            sessionStorage.setItem('userRoles', JSON.stringify(roles));
            sessionStorage.setItem('userRole', user.user_type || 'buyer');
            sessionStorage.setItem('authToken', token);
            localStorage.setItem('authToken', token);
            if(user.avatar_url) sessionStorage.setItem('userAvatar', user.avatar_url);
            window.dispatchEvent(new CustomEvent('flockr-login-success'));
            // GA4 sign_up event (Alex 2026-07-23)
            if (this.$trackEvent) this.$trackEvent('sign_up', { method: 'phone', user_type: user.user_type || 'buyer' });
            this.$router.push('/dashboard');
            return;
          }
          // Fallback for old API shape — keep OTP flow available if needed
          this.userId=(r.data.data&&r.data.data.userId)||'';
          this.step=2;
        }else{
          this.errorMsg=r.data.message||'Registration failed. Please try again.';
        }
      }catch(e){console.error(e);this.errorMsg='Connection error. Please try again.'}
      this.submitting=false
    },
    async verifyOtp(){
      this.verifying=true;this.otpError='';
      try{
        var phone=this.formatPhone(this.form.phone);
        var r=await executeJson({function:"verifyOTP",phone:phone,code:this.otpCode,purpose:'registration',deviceType:'web'},flocker);
        if(r.data.success){
          // Server now returns user+token on successful registration verification —
          // store the session and drop the user straight into the app (no manual sign-in).
          var user = r.data.data && r.data.data.user;
          var token = r.data.data && r.data.data.token;
          if(user && token){
            var roles = (r.data.data.roles)||[];
            sessionStorage.setItem('userId', user.id);
            sessionStorage.setItem('isLoggedIn', 'true');
            sessionStorage.setItem('userName', user.display_name || '');
            sessionStorage.setItem('userPhone', user.phone || '');
            sessionStorage.setItem('userType', user.user_type || '');
            sessionStorage.setItem('userRoles', JSON.stringify(roles));
            sessionStorage.setItem('userRole', user.user_type || 'buyer');
            sessionStorage.setItem('authToken', token);
            localStorage.setItem('authToken', token);
            if(user.avatar_url) sessionStorage.setItem('userAvatar', user.avatar_url);
            this.$router.push('/dashboard');
            return;
          }
          // Fallback for old API behaviour
          this.step=3;
        }
        else{this.otpError=r.data.message||'Invalid OTP. Please try again.'}
      }catch(e){console.error(e);this.otpError='Connection error. Please try again.'}
      this.verifying=false
    },
    async initHCaptcha(){
      try{
        var r=await executeJson({function:'getPublicSettings'},flocker);
        var s=(r.data&&r.data.success&&r.data.data&&r.data.data.settings)||{};
        if(s.hcaptcha_enabled==='1' && s.hcaptcha_site_key){
          this.hcaptchaSiteKey=s.hcaptcha_site_key;
          var self=this;
          // Load the script with explicit-render mode so we control when the widget mounts.
          // Auto-scan mode misses the container on mobile if the script wins the race.
          if(!document.querySelector('script[src*="hcaptcha.com/1/api.js"]')){
            var sc=document.createElement('script');
            sc.src='https://js.hcaptcha.com/1/api.js?render=explicit';
            sc.async=true;sc.defer=true;
            sc.onload=function(){self.ensureHCaptchaWidget();};
            document.head.appendChild(sc);
          } else {
            this.ensureHCaptchaWidget();
          }
        }
      }catch(e){console.warn('hCaptcha init failed', e);}
    },
    ensureHCaptchaWidget(){
      // Retry a few times to cover both "Vue hasn't painted the div yet" and
      // "hcaptcha global not attached yet" — either can flip within a tick or two.
      var self=this, tries=0;
      var tryRender=function(){
        tries++;
        var el=self.$refs.hcaptchaContainer||document.getElementById('hcaptcha-container');
        if(window.hcaptcha && el && !el.dataset.hcaptchaRendered){
          try{
            window.hcaptcha.render(el,{
              sitekey:self.hcaptchaSiteKey,
              theme:'dark',
              callback:function(token){self.hcaptchaToken=token;},
              'expired-callback':function(){self.hcaptchaToken='';}
            });
            el.dataset.hcaptchaRendered='1';
          }catch(e){console.warn('hCaptcha render failed',e);}
          return;
        }
        if(tries<25) setTimeout(tryRender,200);
      };
      this.$nextTick(tryRender);
    },
    async initGoogleSignIn(){
      try{
        var r=await executeJson({function:'getGoogleClientId'},flocker);
        if(!(r.data&&r.data.success&&r.data.data&&r.data.data.enabled))return;
        this.googleClientId=r.data.data.clientId;
        this.googleEnabled=true;
        await this.loadGoogleScript();
        var self=this;
        if(!window.google||!window.google.accounts||!window.google.accounts.id)return;
        window.google.accounts.id.initialize({client_id:this.googleClientId,callback:function(resp){self.onGoogleCredential(resp)}});
        this.$nextTick(function(){
          if(self.$refs.googleBtn) window.google.accounts.id.renderButton(self.$refs.googleBtn,{theme:'outline',size:'large',width:320,text:'signup_with',shape:'pill'});
        });
      }catch(e){}
    },
    loadGoogleScript(){
      return new Promise(function(resolve){
        if(window.google&&window.google.accounts&&window.google.accounts.id)return resolve();
        var existing=document.getElementById('google-id-script');
        if(existing){existing.addEventListener('load',resolve);return}
        var s=document.createElement('script');
        s.src='https://accounts.google.com/gsi/client';
        s.async=true;s.defer=true;s.id='google-id-script';
        s.onload=resolve;s.onerror=resolve;
        document.head.appendChild(s);
      });
    },
    async onGoogleCredential(resp){
      if(!resp||!resp.credential)return;
      this.googleBusy=true;this.googleError='';
      try{
        var r=await executeJson({function:'loginWithGoogle',idToken:resp.credential},flocker);
        if(r.data&&r.data.success&&r.data.data&&r.data.data.user){
          var u=r.data.data.user;
          sessionStorage.setItem('userId',u.id);
          sessionStorage.setItem('userName',u.display_name||(u.first_name+' '+u.last_name));
          sessionStorage.setItem('userType',u.user_type||'buyer');
          sessionStorage.setItem('isLoggedIn','1');
          if(u.avatar_url) sessionStorage.setItem('userAvatar',u.avatar_url);
          if(u.needs_phone==1) sessionStorage.setItem('flockr_needs_phone','1');
          window.dispatchEvent(new CustomEvent('flockr-login-success'));
          this.$router.push(u.needs_phone==1?'/me':'/dashboard');
        }else{
          this.googleError=(r.data&&r.data.message)||'Google sign-up failed';
        }
      }catch(e){this.googleError='Network error during Google sign-up'}
      this.googleBusy=false;
    }
  }
};
</script>
